import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { uploadCronometerCsv } from '../lib/api.js';

/**
 * Compact Cronometer CSV fallback — single row with drag-drop, file picker, and paste details.
 * Auto-imports on drop/pick.
 */
export function CronometerFallback({ onDone }: { onDone: () => void }) {
  const [csv, setCsv] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const upload = async (content: string) => {
    if (!content.trim()) return;
    setSending(true);
    setMsg(null);
    try {
      const r = await uploadCronometerCsv(content);
      setMsg(`imported ${r.written} day${r.written === 1 ? '' : 's'}`);
      onDone();
    } catch (e) {
      setMsg(`error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSending(false);
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsv(text);
    await upload(text);
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsv(text);
    await upload(text);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`relative border px-4 py-2 flex items-center gap-3 text-xs font-mono transition-colors ${
        dragOver ? 'border-amber-400 bg-amber-950/40' : 'border-amber-900/60 bg-amber-950/10'
      }`}
    >
      <Upload size={14} className="text-amber-400 shrink-0" />
      <span className="text-amber-400 uppercase tracking-[0.2em] text-[10px] shrink-0">cronometer offline</span>
      <span className="text-stone-400 truncate">
        {sending ? 'importing…' : dragOver ? 'release to import' : 'drop a CSV here'}
      </span>
      <button
        onClick={() => fileRef.current?.click()}
        className="text-stone-300 hover:text-stone-100 underline underline-offset-2 shrink-0"
      >
        browse
      </button>
      <span className="text-stone-700">·</span>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-stone-400 hover:text-stone-200 shrink-0"
      >
        {expanded ? 'hide paste' : 'paste'}
      </button>
      {msg && <span className="text-stone-300 ml-auto shrink-0">{msg}</span>}
      <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onPickFile} />

      {expanded && (
        <div className="absolute left-0 right-0 top-full mt-2 p-3 bg-stone-900 border border-stone-700 z-10">
          <textarea
            className="w-full bg-stone-950 border border-stone-700 p-2 font-mono text-xs text-stone-200 h-24 focus:border-stone-500 focus:outline-none"
            placeholder="paste CSV text…"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
          <button
            onClick={() => void upload(csv)}
            disabled={!csv.trim() || sending}
            className="mt-2 px-3 py-1 bg-stone-100 text-stone-950 text-[10px] uppercase tracking-[0.2em] font-mono hover:bg-stone-300 disabled:opacity-50"
          >
            {sending ? 'importing…' : 'import'}
          </button>
        </div>
      )}
    </div>
  );
}
